import {
  Divider,
  Heading,
  Stack,
  Spinner,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { KYDScope } from "@common/interfaces/ICognito";
import { useCallback, useState } from "react";
import actions from "../../actions";
import { useDialog } from "../../Common/Dialog";
import MarketingPixelsEditor from "../MarketingPixelsEditor";
import MetaAdsSetting from "../MetaAdsSetting";
import { useFacebookMetaAdsConnect } from "../hooks/useFacebookMetaAdsConnect";
import { useOrganizationMarketingSettings } from "../hooks/useOrganizationMarketingSettings";

const Marketing = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const { organization, loading, refetch, updatePixels } =
    useOrganizationMarketingSettings();
  const canManageMetaAds =
    permissions.includes("organizations/write") ||
    permissions.includes("organizations/bio:write");
  const [localLoading, setLocalLoading] = useState({
    meta: false,
    pixels: false,
  });
  const toast = useToast();
  const dialog = useDialog();

  const updateLoading = useCallback(
    (key: keyof typeof localLoading, value: boolean) => {
      setLocalLoading((current) => ({ ...current, [key]: value }));
    },
    []
  );

  const { triggerFacebookLogin } = useFacebookMetaAdsConnect({
    organization,
    toast,
    dialog,
    onAccessGranted: async () => {
      await refetch(true);
    },
    onLoadingChange: (value) => updateLoading("meta", value),
  });

  const removeOrganizationAccess = useCallback(async () => {
    updateLoading("meta", true);
    try {
      await actions.ADMIN.removeOrganizationAccess();
      toast({
        status: "success",
        title: "Access removed",
        description:
          "KYD access to Meta ads has been removed. You can no longer manage campaigns via KYD dashboard",
      });
      await refetch(true);
    } catch (error) {
      dialog({ text: error.message });
    } finally {
      updateLoading("meta", false);
    }
  }, [dialog, refetch, toast, updateLoading]);

  return (
    <VStack w="100%">
      <Stack maxW="1000px" py={[2, 6]} spacing={{ base: 8, lg: 6 }} w="100%">
        <Heading
          fontWeight="medium"
          size={useBreakpointValue({ base: "xs", lg: "sm" })}
        >
          <Text fontSize="lg" fontWeight="bold">
            Marketing Settings
          </Text>
        </Heading>

        {loading.organization && !organization && (
          <VStack>
            <Spinner />
            <Text>Loading marketing settings...</Text>
          </VStack>
        )}

        {organization && (
          <Stack spacing={8}>
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Text fontSize="lg" fontWeight="medium">
                  Marketing Pixels
                </Text>
                <Text color="gray.600" fontSize="sm">
                  Applied to every event in this organization. Conversion API
                  access tokens are hidden after they are saved.
                </Text>
              </Stack>
              <MarketingPixelsEditor
                isLoading={localLoading.pixels}
                onSave={async (pixels) => {
                  updateLoading("pixels", true);
                  try {
                    const success = await updatePixels(pixels);
                    if (!success) {
                      throw new Error("Unable to update marketing pixels");
                    }
                    toast({
                      title: "Marketing pixels updated",
                      status: "success",
                      duration: 5000,
                      isClosable: true,
                    });
                  } finally {
                    updateLoading("pixels", false);
                  }
                }}
                pixels={organization.marketing_pixels || []}
                readOnly={
                  !permissions.includes("organizations/write") &&
                  !permissions.includes("organizations/bio:write")
                }
              />
            </Stack>

            {canManageMetaAds && (
              <>
                <Divider />
                <MetaAdsSetting
                  show={!!organization.ads_manager?.config_id}
                  organization={organization}
                  onConnect={triggerFacebookLogin}
                  onDisconnect={removeOrganizationAccess}
                  isLoading={localLoading.meta}
                />
              </>
            )}
          </Stack>
        )}
      </Stack>
    </VStack>
  );
};

export default Marketing;
